import React from "react";
import Label from "@/components/form/Label";

// REQUIREMENTS SUMMARY:
// - Enhanced ColorPickerField to ensure the color swatch fills the entire container.
// - Added browser-specific pseudo-element styles to remove internal padding and borders.

interface ColorPickerFieldProps {
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean | string;
  hint?: string;
  className?: string;
  disabled?: boolean;
}

const ColorPickerField = React.forwardRef<HTMLInputElement, ColorPickerFieldProps>(({
  id,
  name,
  value,
  onChange,
  onBlur,
  label,
  placeholder = "#000000",
  required = false,
  error = false,
  hint,
  className = "",
  disabled = false,
}, ref) => {
  // Build input classes using TailAdmin styling
  const getInputClasses = () => {
    let baseClasses = "h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-base shadow-sm placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30";
    
    if (disabled) {
      return `${baseClasses} text-gray-500 border-gray-300 opacity-40 bg-gray-100 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700`;
    } else if (error) {
      return `${baseClasses} border-error-500 focus:border-error-300 focus:ring-error-500/20 dark:text-error-400 dark:border-error-500 dark:focus:border-error-800`;
    } else {
      return `${baseClasses} bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700 dark:text-white/90 dark:focus:border-brand-800`;
    }
  };

  const getColorSwatchClasses = () => {
    let baseClasses = "h-11 w-14 sm:w-20 rounded-lg border cursor-pointer shadow-sm focus:outline-hidden focus:ring-3 p-0 overflow-hidden [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:border-none";
    
    if (disabled) {
      return `${baseClasses} border-gray-300 opacity-40 cursor-not-allowed dark:border-gray-700`;
    } else if (error) {
      return `${baseClasses} border-error-500 focus:border-error-300 focus:ring-error-500/20 dark:border-error-500 dark:focus:border-error-800`;
    } else {
      return `${baseClasses} border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700 dark:focus:border-brand-800`;
    }
  };

  return (
    <div className={className}>
      {label && (
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
      )}
      
      <div className="flex gap-2 sm:gap-3 items-center">
        {/* Color Swatch Input */}
        <input
          id={`${id}_swatch`}
          name={`${name}_swatch`}
          type="color"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          className={getColorSwatchClasses()}
          aria-label={label ? `${label} color swatch` : "Color swatch"}
        />
        
        {/* Hex Value Input */}
        <input
          ref={ref}
          id={id}
          name={name}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          disabled={disabled}
          maxLength={7}
          pattern="^#[0-9A-Fa-f]{6}$"
          className={`${getInputClasses()} flex-1 min-w-0`}
          aria-label={label ? `${label} hex value` : "Color hex value"}
        />
      </div>
      
      {hint && !error && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {hint}
        </p>
      )}
      
      {error && typeof error === "string" && (
        <p className="mt-1 text-sm text-error-500 dark:text-error-400">
          {error}
        </p>
      )}
    </div>
  );
});

ColorPickerField.displayName = "ColorPickerField";

export default ColorPickerField;

